import React, { useEffect, useRef, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { api, API, fileUrl } from "@/lib/api";
import { Send, Sparkles, Mic, Square, ArrowUpRight, Loader2 } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import { ChatBubble } from "@/components/ChatWidget";

export default function AskHeda() {
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      english:
        "Hi, I'm Heda AI — your showroom assistant. Ask me anything about Heda Plywoods: 'I need exterior louvers for galleries', 'fire-rated panels for kitchen', 'water-resistant laminates for bathroom', or speak in Hindi, Marathi, Kannada or any language. I'll find the right products.",
      original: null,
      language: "en",
      language_label: "EN",
      referenced_products: [],
    },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const nav = useNavigate();

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || sending) return;
    setInput("");
    const newHistory = [
      ...messages,
      { role: "user", english: q, original: q, language: "auto" },
    ];
    setMessages(newHistory);
    setSending(true);
    try {
      const { data } = await api.post("/ask-heda", {
        message: q,
        history: newHistory
          .slice(-6)
          .map((m) => ({ role: m.role, content: m.english || m.original || "" })),
      });
      setMessages((m) => [...m, { role: "assistant", ...data }]);
    } catch (e) {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          english:
            e?.response?.data?.detail || "Couldn't reach Heda AI right now. Please try again.",
          original: null,
          language: "en",
          language_label: "EN",
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  const sendVoice = async (blob) => {
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("audio", blob, "voice.webm");
      fd.append(
        "history",
        JSON.stringify(
          messages
            .slice(-6)
            .map((m) => ({ role: m.role, content: m.english || m.original || "" })),
        ),
      );
      const token = localStorage.getItem("heda_token");
      const res = await fetch(`${API}/ask-heda/voice`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setMessages((m) => [
        ...m,
        {
          role: "user",
          english: data.transcript,
          original: data.transcript,
          language: data.language,
          language_label: data.language_label,
        },
        { role: "assistant", ...data },
      ]);
    } catch (e) {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          english:
            "Couldn't transcribe the voice — try again or type the question.",
          original: null,
          language: "en",
          language_label: "EN",
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  return (
    <AdminLayout>
      <div className="max-w-3xl mx-auto h-[calc(100vh-180px)] flex flex-col">
        <div className="mb-4">
          <div className="hed-overline">Ask Heda</div>
          <h1 className="font-serif text-3xl md:text-4xl mt-1 tracking-tight">
            Anything across your whole catalog.
          </h1>
          <p className="text-sm text-[#5C5C54] mt-1.5">
            Type or speak in any language. Heda AI searches every product, every PDF, every research note — and recommends the right match with a tap-to-open link.
          </p>
        </div>

        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto bg-[#FAFAF8] border border-[#E8E6DF] rounded-sm p-4 space-y-3 mb-3"
        >
          {messages.map((m, idx) => (
            <div key={idx}>
              <ChatBubble message={m} />
              {m.role === "assistant" && m.referenced_products?.length > 0 && (
                <div className="mt-2 ml-1 grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {m.referenced_products.map((p) => (
                    <Link
                      key={p.id}
                      to={p.code ? `/admin/products/${p.id}/edit` : `/admin/products/${p.id}/edit`}
                      className="group flex items-center gap-2 border border-[#E8E6DF] hover:border-[#B5953B] bg-white rounded-sm p-2 transition-colors"
                      data-testid={`ask-heda-product-card-${p.id}`}
                    >
                      {p.thumb ? (
                        <img
                          src={fileUrl(p.thumb)}
                          alt={p.name}
                          className="w-10 h-10 object-cover rounded-sm flex-shrink-0"
                        />
                      ) : (
                        <div className="w-10 h-10 bg-[#F2F0EA] flex items-center justify-center flex-shrink-0 rounded-sm">
                          <Sparkles size={14} className="text-[#B5953B]" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-semibold text-[#1A1A18] truncate">
                          {p.name}
                        </div>
                        <div className="text-[10px] uppercase tracking-wider text-[#5C5C54] truncate">
                          {p.category || p.code || "View →"}
                        </div>
                      </div>
                      <ArrowUpRight
                        size={12}
                        className="text-[#5C5C54] group-hover:text-[#B5953B] flex-shrink-0"
                      />
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
          {sending && (
            <div className="flex justify-start">
              <div className="hed-typing bg-[#F2F0EA] border border-[#E8E6DF] px-4 py-3 rounded-2xl rounded-bl-md">
                <span></span><span></span><span></span>
              </div>
            </div>
          )}
        </div>

        <form
          className="flex items-center gap-2 bg-white border border-[#E8E6DF] rounded-full px-2 py-2"
          onSubmit={(e) => { e.preventDefault(); send(); }}
        >
          <AskMicButton onAudio={sendVoice} disabled={sending} />
          <input
            data-testid="ask-heda-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Tell me what you need…"
            className="flex-1 min-w-0 px-3 py-2 text-sm outline-none bg-transparent"
          />
          <button
            data-testid="ask-heda-send"
            type="submit"
            disabled={sending || !input.trim()}
            className="bg-[#B5953B] hover:bg-[#9A7E32] disabled:opacity-40 text-white p-2.5 rounded-full transition-colors flex-shrink-0"
          >
            <Send size={16} strokeWidth={2} />
          </button>
        </form>
      </div>
    </AdminLayout>
  );
}

function AskMicButton({ onAudio, disabled }) {
  const [recording, setRecording] = useState(false);
  const recRef = useRef(null);
  const chunksRef = useRef([]);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: "audio/webm" });
      recRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        stream.getTracks().forEach((t) => t.stop());
        if (blob.size > 1000) onAudio(blob);
      };
      mr.start();
      setRecording(true);
    } catch (e) {
      alert("Microphone access denied. Allow microphone access to use voice search.");
    }
  };
  const stop = () => {
    if (recRef.current && recRef.current.state !== "inactive") recRef.current.stop();
    setRecording(false);
  };

  return (
    <button
      type="button"
      onClick={recording ? stop : start}
      disabled={disabled && !recording}
      data-testid={recording ? "ask-heda-mic-stop" : "ask-heda-mic-start"}
      className={`p-2.5 rounded-full transition-colors flex-shrink-0 ${
        recording
          ? "bg-[#9E2A2B] hover:bg-[#7d2122] text-white animate-pulse"
          : "bg-[#F2F0EA] hover:bg-[#E8E6DF] text-[#1A1A18] border border-[#E8E6DF]"
      } disabled:opacity-40`}
    >
      {recording ? <Square size={14} strokeWidth={2} /> : <Mic size={16} strokeWidth={2} />}
    </button>
  );
}
